import React, { useState, useEffect, useRef } from "react";
import Notification from "./Notification";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch } from "../lib/api";
import "./Dashboard.css";
import TripPathMap from "./TripPathMap";

// Date formatting helpers
const pad2 = (n: number) => n.toString().padStart(2, "0");
const toDate = (v: string | Date | null | undefined): Date | null => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};
export const formatDate = (v: string | Date | null | undefined): string => {
  const d = toDate(v);
  if (!d) return "-";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};
export const formatDateTime = (v: string | Date | null | undefined): string => {
  const d = toDate(v);
  if (!d) return "-";
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return `${formatDate(d)} ${time}`;
};

interface Trip {
  id: string;
  rideCode: string;
  startTime: string;
  startLocation: string;
  startLocationName?: string | null;
  endTime?: string;
  endLocation?: string;
  endLocationName?: string | null;
  state: string;
  driver: {
    id?: string;
    name: string;
    email: string;
  };
  cab: {
    plateNumber: string;
    capacity: number;
  };
}

interface UserTrip {
  id: string;
  status: string;
  boardingTime: string;
  boardingLocation: string;
  boardingLocationName?: string | null;
  alightingTime?: string;
  alightingLocation?: string;
  alightingLocationName?: string | null;
}

const RiderDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [notice, setNotice] = useState<{ kind: 'success' | 'error' | 'info'; message: string } | null>(null);
  const showNotice = (message: string, kind: 'success' | 'error' | 'info' = 'info') => setNotice({ kind, message });
  const [rideCode, setRideCode] = useState<string>("");
  // Locations are now derived from the trip's latest logged coordinates server-side
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentTrip, setCurrentTrip] = useState<Trip | null>(null);
  const [userTrip, setUserTrip] = useState<UserTrip | null>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [isScannerOpen, setIsScannerOpen] = useState<boolean>(false);
  const scannerRef = useRef<any>(null);
  const [confirmAlightOpen, setConfirmAlightOpen] = useState<boolean>(false);

  // Live location browsing state
  type OngoingTrip = Trip & { locationLogs?: Array<{ location: string; timestamp: string }>; joinedCount?: number };
  const [ongoingTrips, setOngoingTrips] = useState<OngoingTrip[]>([]);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  // Ongoing-browse route state (used for viewing any trip's full path, including currentTrip)
  const [selectedTripCoords, setSelectedTripCoords] = useState<Array<[number, number]>>([]);
  const browseLogsPollRef = useRef<number | null>(null);
  const [showProfile, setShowProfile] = useState<boolean>(false);
  const [showActivity, setShowActivity] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [showTripDetails, setShowTripDetails] = useState<boolean>(false);
  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [reviewRating, setReviewRating] = useState<number>(0);
  const [reviewComment, setReviewComment] = useState<string>("");
  const [isSubmittingReview, setIsSubmittingReview] = useState<boolean>(false);
  const [reviewError, setReviewError] = useState<string>("");
  // Track last-known active status and whether review shown for a trip
  const lastKnownActiveRef = useRef<boolean>(false);
  const reviewShownForTripIdRef = useRef<string | null>(null);
  const riderActivePollRef = useRef<number | null>(null);
  // When opening review from history, we target a specific driver
  const [pendingReviewTarget, setPendingReviewTarget] = useState<{ driverId: string; driverName?: string; tripId?: string } | null>(null);

  // Activity (history) state
  interface HistoryTrip {
    id: string;
    serviceDate: string;
    startTime: string;
    startLocation: string;
    startLocationName?: string | null;
    endTime?: string | null;
    endLocation?: string | null;
    endLocationName?: string | null;
    state: string;
    // Rider-specific fields from backend (when available)
    riderBoardingTime?: string | null;
    riderBoardingLocation?: string | null;
    riderBoardingLocationName?: string | null;
    riderAlightingTime?: string | null;
    riderAlightingLocation?: string | null;
    riderAlightingLocationName?: string | null;
    riderStatus?: string;
  }
  const [tripHistory, setTripHistory] = useState<HistoryTrip[]>([]);
  const [selectedHistoryTrip, setSelectedHistoryTrip] = useState<HistoryTrip | null>(null);
  const [historyTripLogs, setHistoryTripLogs] = useState<Array<{ location: string; timestamp: string }>>([]);
  const [historyMetrics, setHistoryMetrics] = useState<{ distanceKm: number; durationMin: number } | null>(null);
  const [historyTripDetails, setHistoryTripDetails] = useState<Trip | null>(null);
  const [historyReview, setHistoryReview] = useState<{ rating: number; comment?: string; createdAt?: string } | null>(null);
  // Rider-specific userTrip for the selected history trip
  interface HistoryUserTrip {
    boardingTime?: string | null;
    boardingLocation?: string | null;
    boardingLocationName?: string | null;
    alightingTime?: string | null;
    alightingLocation?: string | null;
    alightingLocationName?: string | null;
    status?: string;
  }
  const [historyUserTrip, setHistoryUserTrip] = useState<HistoryUserTrip | null>(null);

  const parseLocation = (loc?: string | null): { lat: number; lng: number } | null => {
    if (!loc) return null;
    const parts = loc.split(",").map((s) => parseFloat(s.trim()));
    if (parts.length !== 2 || parts.some((x) => Number.isNaN(x))) return null;
    return { lat: parts[0], lng: parts[1] };
  };

  // Start QR scanner when modal opens (using html5-qrcode)
  useEffect(() => {
    const startScanner = async () => {
      if (!isScannerOpen) return;
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        const elementId = 'qr-reader';
        scannerRef.current = new Html5Qrcode(elementId);

        const onSuccess = (decodedText: string) => {
          if (decodedText) {
            setRideCode(decodedText.trim());
            setIsScannerOpen(false);
            setTimeout(() => {
              if (!isLoading && user?.id) {
                joinTrip();
              }
            }, 0);
          }
        };

        // Attempt 1: Back camera (mobile)
        try {
          await scannerRef.current.start(
            { facingMode: { exact: 'environment' } },
            { fps: 10, qrbox: 250 },
            onSuccess,
            () => {}
          );
          return; // success
        } catch (err1) {
          console.warn('Environment camera not available, falling back...', err1);
        }

        // Attempt 2: First available camera (desktop/laptop)
        try {
          const cameras = await Html5Qrcode.getCameras();
          if (cameras && cameras.length > 0) {
            await scannerRef.current.start(
              cameras[0].id,
              { fps: 10, qrbox: 250 },
              onSuccess,
              () => {}
            );
            return; // success
          }
        } catch (err2) {
          console.warn('Could not enumerate cameras, trying user-facing...', err2);
        }

        // Attempt 3: User/front camera
        await scannerRef.current.start(
          { facingMode: 'user' },
          { fps: 10, qrbox: 250 },
          onSuccess,
          () => {}
        );
      } catch (err) {
        console.error('QR scanner failed to start:', err);
        showNotice('Unable to start camera. Please enter the ride code manually.', 'error');
        setIsScannerOpen(false);
      }
    };

    startScanner();

    return () => {
      const stop = async () => {
        if (scannerRef.current) {
          try { await scannerRef.current.stop(); } catch {}
          try { await scannerRef.current.clear(); } catch {}
          scannerRef.current = null;
        }
      };
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScannerOpen]);

  // Fetch list of ongoing trips periodically for browsing
  useEffect(() => {
    let cancel = false;
    const fetchOngoing = async () => {
      try {
        const res = await apiFetch("/trips/ongoing");
        const data = await res.json();
        if (!cancel && data.success) {
          setOngoingTrips(data.trips || []);
        }
      } catch (e) {
        // no-op
      }
    };
    fetchOngoing();
    const interval = window.setInterval(fetchOngoing, 15000);
    return () => {
      cancel = true;
      window.clearInterval(interval);
    };
  }, []);

  // When an ongoing trip is selected in browse list, fetch and poll its full route logs
  useEffect(() => {
    const stop = () => {
      if (browseLogsPollRef.current != null) {
        window.clearInterval(browseLogsPollRef.current);
        browseLogsPollRef.current = null;
      }
    };

    // Reset when nothing selected
    if (!selectedTripId) {
      stop();
      setSelectedTripCoords([]);
      return;
    }

    const fetchLogs = async () => {
      try {
        const res = await apiFetch(`/trips/${selectedTripId}/location-logs`);
        const data = await res.json();
        if (data?.success) {
          const logs = (data.logs as Array<{ location: string; timestamp: string }>) || [];
          const coords: Array<[number, number]> = logs
            .map(l => parseLocation(l.location) || null)
            .filter((c): c is { lat: number; lng: number } => !!c)
            .map(c => [c.lat, c.lng]);
          setSelectedTripCoords(coords);
        }
      } catch {
        // ignore transient errors
      }
    };

    // Initial and interval
    fetchLogs();
    stop();
    browseLogsPollRef.current = window.setInterval(fetchLogs, 5000) as unknown as number;
    return () => stop();
  }, [selectedTripId]);

  // Fetch rider activity history when Activity view is opened
  useEffect(() => {
    const fetchHistory = async () => {
      if (!showActivity || !user?.id) return;
      try {
        const res = await apiFetch(`/trips/rider/${user.id}/history`);
        const data = await res.json();
        if (data?.success) {
          setTripHistory(data.trips || []);
        }
      } catch (e) {
        // ignore
      }
    };
    fetchHistory();
  }, [showActivity, user?.id]);

  // When a history trip is selected, fetch its logs and details (metrics computed in a separate effect)
  useEffect(() => {
    const fetchLogs = async () => {
      if (!selectedHistoryTrip) {
        setHistoryTripLogs([]);
        setHistoryMetrics(null);
        setHistoryTripDetails(null);
        setHistoryReview(null);
        setHistoryUserTrip(null);
        return;
      }
      try {
        const res = await apiFetch(`/trips/${selectedHistoryTrip.id}/location-logs`);
        const data = await res.json();
        if (data?.success) {
          const logs = data.logs as Array<{ location: string; timestamp: string }>;
          setHistoryTripLogs(logs);
        } else {
          setHistoryTripLogs([]);
          setHistoryMetrics(null);
        }
      } catch (e) {
        setHistoryTripLogs([]);
        setHistoryMetrics(null);
      }

      // Also fetch trip details to identify driver for review and rider's own userTrip details, then fetch review from backend
      try {
        const detRes = await apiFetch(`/trips/${selectedHistoryTrip.id}`);
        const det = await detRes.json();
        if (det?.success && det.trip) {
          setHistoryTripDetails(det.trip as Trip);
          // From trip details, derive the current rider's own userTrip (boarding/alighting info)
          try {
            const allUserTrips: Array<any> | undefined = (det.trip as any)?.userTrips;
            if (allUserTrips && user?.id) {
              const mine = allUserTrips.find((ut: any) => ut?.userId === user.id || ut?.user?.id === user.id);
              if (mine) {
                setHistoryUserTrip({
                  boardingTime: mine.boardingTime ?? null,
                  boardingLocation: mine.boardingLocation ?? null,
                  alightingTime: mine.alightingTime ?? null,
                  alightingLocation: mine.alightingLocation ?? null,
                  status: mine.status,
                });
              } else {
                setHistoryUserTrip(null);
              }
            } else {
              setHistoryUserTrip(null);
            }
          } catch {
            setHistoryUserTrip(null);
          }
          const dId: string | undefined = (det.trip?.driver && det.trip.driver.id) ? det.trip.driver.id : (det.trip as any)?.driverId;
          if (dId) {
            try {
              const rRes = await apiFetch(`/reviews/mine?aboutId=${encodeURIComponent(dId)}&tripId=${encodeURIComponent(selectedHistoryTrip.id)}`);
              const rData = await rRes.json();
              if (rData?.success && rData.review) {
                setHistoryReview({ rating: rData.review.rating, comment: rData.review.comment ?? undefined, createdAt: rData.review.createdAt });
              } else {
                setHistoryReview(null);
              }
            } catch {
              setHistoryReview(null);
            }
          } else {
            setHistoryReview(null);
          }
        } else {
          setHistoryTripDetails(null);
          setHistoryReview(null);
        }
      } catch {
        setHistoryTripDetails(null);
        setHistoryUserTrip(null);
        setHistoryReview(null);
      }
    };
    fetchLogs();
  }, [selectedHistoryTrip, user?.id]);

  // Compute rider-specific distance and duration using rider's own segment (boarding -> alighting)
  useEffect(() => {
    if (!selectedHistoryTrip) {
      setHistoryMetrics(null);
      return;
    }

    const toCoords = (loc: string | null | undefined) => {
      const p = parseLocation(loc ?? undefined);
      return p ? [p.lat, p.lng] as [number, number] : null;
    };
    const haversineKm = (a: [number, number], b: [number, number]) => {
      const R = 6371; // km
      const toRad = (d: number) => (d * Math.PI) / 180;
      const dLat = toRad(b[0] - a[0]);
      const dLon = toRad(b[1] - a[1]);
      const lat1 = toRad(a[0]);
      const lat2 = toRad(b[0]);
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(h));
    };

    try {
      const logs = historyTripLogs || [];
      // Determine rider segment window
      const riderStartTs = historyUserTrip?.boardingTime || selectedHistoryTrip.riderBoardingTime || selectedHistoryTrip.startTime;
      const riderEndTs = historyUserTrip?.alightingTime || selectedHistoryTrip.riderAlightingTime || selectedHistoryTrip.endTime || logs[logs.length - 1]?.timestamp || riderStartTs;

      // Filter logs within rider segment
      const startMs = new Date(riderStartTs as string).getTime();
      const endMs = new Date(riderEndTs as string).getTime();
      const slice = logs.filter(l => {
        const t = new Date(l.timestamp).getTime();
        return t >= startMs && t <= endMs;
      });

      // Build coordinate list from slice; if empty, try to include closest surrounding points
      let coords: [number, number][] = [];
      for (const l of slice) {
        const c = toCoords(l.location);
        if (c) coords.push(c);
      }
      // If coords insufficient, fallback to using overall logs but still compute duration via rider times
      if (coords.length < 2) {
        for (const l of logs) {
          const c = toCoords(l.location);
          if (c) coords.push(c);
        }
      }

      // Compute distance
      let distance = 0;
      for (let i = 1; i < coords.length; i++) {
        distance += haversineKm(coords[i - 1], coords[i]);
      }
      const distanceKm = Math.round(distance * 10) / 10;

      // Compute duration strictly from rider's times when available
      const durationMin = Math.max(0, Math.round((endMs - startMs) / 60000));
      setHistoryMetrics({ distanceKm, durationMin });
    } catch {
      setHistoryMetrics(null);
    }
  }, [historyTripLogs, selectedHistoryTrip, historyUserTrip]);

  // API calls
  const joinTrip = async () => {
    if (!rideCode || !user?.id) return;

    setIsLoading(true);
    try {
      // Join the trip using the new join-by-code endpoint
      const joinResponse = await apiFetch("/trips/join-by-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rideCode, userId: user.id }),
      });

      const joinResult = await joinResponse.json();
      if (joinResult.success) {
        // Get trip details after successful join
        const tripResponse = await apiFetch(`/trips/${joinResult.userTrip.tripId}`);
        const tripResult = await tripResponse.json();
        
        if (tripResult.success) {
          setCurrentTrip(tripResult.trip);
          setUserTrip(joinResult.userTrip);
          setRideCode("");
          // No manual boarding location to reset
          showNotice("Successfully joined the trip!", 'success');
        }
      } else {
        showNotice(`Error: ${joinResult.error}`, 'error');
      }
    } catch (error) {
      showNotice("Failed to join trip. Please try again.", 'error');
    }
    setIsLoading(false);
  };

  const alightFromTrip = async () => {
    if (!currentTrip?.id || !user?.id) {
      showNotice('Trip not found or not ongoing.', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiFetch("/trips/alight", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tripId: currentTrip.id, userId: user.id, alightingLocation: null }),
      });

      const result = await response.json();
      if (result.success) {
        setUserTrip(result.userTrip);
        // Prompt for review after alighting
        setShowReviewModal(true);
        // Mark review shown for this trip to avoid duplicate prompts from polling
        if (currentTrip?.id) {
          reviewShownForTripIdRef.current = currentTrip.id;
        }
        // Stop rider active polling; no need to continue after rider ends
        if (riderActivePollRef.current != null) {
          window.clearInterval(riderActivePollRef.current);
          riderActivePollRef.current = null;
        }
      } else {
        showNotice(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      showNotice("Failed to alight from trip. Please try again.", 'error');
    }
    setIsLoading(false);
  };

  const submitReview = async () => {
    if (!user?.id) return;
    const driverId = pendingReviewTarget?.driverId || (currentTrip as any)?.driver?.id || (currentTrip as any)?.driverId;
    const tripIdForReview = pendingReviewTarget?.tripId || (currentTrip as any)?.id;
    if (!driverId) {
      setReviewError("Unable to identify driver for this trip. You can skip for now.");
      return;
    }
    if (reviewRating < 1 || reviewRating > 5) {
      setReviewError("Please select a rating between 1 and 5 stars.");
      return;
    }
    setReviewError("");
    setIsSubmittingReview(true);
    try {
      const res = await apiFetch("/reviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          aboutId: driverId,
          rating: reviewRating,
          comment: reviewComment.trim() || undefined,
          tripId: tripIdForReview,
        }),
      });
      const data = await res.json();
      if (data?.success) {
        // If we were reviewing from history modal, update local state and just close the review dialog
        if (pendingReviewTarget) {
          setHistoryReview({ rating: reviewRating, comment: reviewComment.trim() || undefined, createdAt: new Date().toISOString() });
          setPendingReviewTarget(null);
          setShowReviewModal(false);
          setReviewRating(0);
          setReviewComment("");
          return;
        }

        // Default flow (after alighting): close and show summary
        setShowReviewModal(false);
        setReviewRating(0);
        setReviewComment("");
        setShowTripDetails(true);
      } else {
        setReviewError(data?.error || "Failed to submit review. Please try again.");
      }
    } catch (e) {
      setReviewError("Network error while submitting review.");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const closeTripDetails = () => {
    setShowTripDetails(false);
    setCurrentTrip(null);
    setUserTrip(null);
  };

  // Check for active trips on component mount
  useEffect(() => {
    const checkActiveTrip = async () => {
      if (!user?.id) {
        setIsInitializing(false);
        return;
      }

      try {
        // Check if user has any active trips
  const response = await apiFetch(`/trips/rider/${user.id}/active`);
        const result = await response.json();
        
        if (result.success && result.trip && result.userTrip) {
          setCurrentTrip(result.trip);
          setUserTrip(result.userTrip);
          // console.log("Found active trip for rider:", result.trip);
          lastKnownActiveRef.current = true;
        } else {
          // console.log("No active trip found for rider");
          lastKnownActiveRef.current = false;
        }
      } catch (error) {
        console.error("Failed to check for active trips:", error);
      } finally {
        setIsInitializing(false);
      }
    };

    checkActiveTrip();
  }, [user?.id]);

  // Poll for rider's active trip to detect when driver ends trip and trigger review
  useEffect(() => {
    if (!user?.id) return;

    // If review is open or rider is not currently joined, ensure polling is stopped and exit
    if (showReviewModal || userTrip?.status !== 'joined') {
      if (riderActivePollRef.current != null) {
        window.clearInterval(riderActivePollRef.current);
        riderActivePollRef.current = null;
      }
      return;
    }

    // Start polling only when rider is joined on a trip
    if (riderActivePollRef.current != null) {
      window.clearInterval(riderActivePollRef.current);
      riderActivePollRef.current = null;
    }
    lastKnownActiveRef.current = true;
    riderActivePollRef.current = window.setInterval(async () => {
      try {
        const res = await apiFetch(`/trips/rider/${user.id}/active`);
        const result = await res.json();
        if (result.success && result.trip && result.userTrip) {
          // Still active, keep state fresh
          setCurrentTrip(result.trip);
          setUserTrip(result.userTrip);
          lastKnownActiveRef.current = true;
        } else {
          // No longer active, likely ended by driver
          if (
            lastKnownActiveRef.current &&
            currentTrip &&
            !showReviewModal &&
            reviewShownForTripIdRef.current !== currentTrip.id
          ) {
            // Fetch final trip details to derive this rider's userTrip (boarding/alighting info)
            try {
              const detRes = await apiFetch(`/trips/${currentTrip.id}`);
              const det = await detRes.json();
              if (det?.success && det.trip) {
                setCurrentTrip(det.trip as Trip);
                const allUserTrips: Array<any> | undefined = (det.trip as any)?.userTrips;
                if (allUserTrips && user?.id) {
                  const mine = allUserTrips.find((ut: any) => ut?.userId === user.id || ut?.user?.id === user.id);
                  if (mine) {
                    setUserTrip({
                      id: mine.id,
                      status: mine.status,
                      boardingTime: mine.boardingTime,
                      boardingLocation: mine.boardingLocation,
                      boardingLocationName: mine.boardingLocationName ?? undefined,
                      alightingTime: mine.alightingTime ?? undefined,
                      alightingLocation: mine.alightingLocation ?? undefined,
                      alightingLocationName: mine.alightingLocationName ?? undefined,
                    });
                  }
                }
              }
            } catch {
              // non-fatal: modal will still open, may show partial info
            }
            setShowReviewModal(true);
            reviewShownForTripIdRef.current = currentTrip.id;
          }
          // Stop polling once inactive
          if (riderActivePollRef.current != null) {
            window.clearInterval(riderActivePollRef.current);
            riderActivePollRef.current = null;
          }
          lastKnownActiveRef.current = false;
        }
      } catch (e) {
        // ignore transient errors
      }
    }, 5000) as unknown as number;

    return () => {
      if (riderActivePollRef.current != null) {
        window.clearInterval(riderActivePollRef.current);
        riderActivePollRef.current = null;
      }
    };
  }, [user?.id, userTrip?.status, currentTrip, currentTrip?.id, showReviewModal]);

  return (
    <div className="dashboard">
      {notice && (
        <Notification
          kind={notice.kind}
          message={notice.message}
          onClose={() => setNotice(null)}
        />
      )}
      {/* Mobile Menu Bar */}
      <div className="menu-bar">
        <button 
          className="menu-toggle-btn" 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="menu-icon">☰</span>
        </button>
        <div className="menu-title">
          <h1>Cab Service</h1>
          <p>Rider Dashboard</p>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="menu-overlay" onClick={() => setIsMenuOpen(false)}>
          <div className="menu-content" onClick={(e) => e.stopPropagation()}>
            <div className="menu-header">
              <h2>Menu</h2>
              <button 
                className="menu-close-btn" 
                onClick={() => setIsMenuOpen(false)}
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>
            <div className="menu-items">
              <button 
                className="menu-item" 
                onClick={() => {
                  setShowActivity(true);
                  setShowProfile(false);
                  setIsMenuOpen(false);
                }}
              >
                <span className="menu-item-icon">🧭</span>
                <span>Activity</span>
              </button>
              <button 
                className="menu-item" 
                onClick={() => {
                  setShowProfile(true);
                  setShowActivity(false);
                  setIsMenuOpen(false);
                }}
              >
                <span className="menu-item-icon">👤</span>
                <span>View Profile</span>
              </button>
              <button 
                className="menu-item logout-item" 
                onClick={() => {
                  logout();
                  setIsMenuOpen(false);
                }}
              >
                <span className="menu-item-icon">🚪</span>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Header (hide on Profile/Activity pages) */}
      {!showProfile && !showActivity && (
      <div className="dashboard-header">
        <div className="user-info">
          <h1>Welcome, {user?.name}!</h1>
          <p>Rider Dashboard</p>
        </div>
      </div>
      )}

      <div className="dashboard-content">
        {showProfile ? (
          // Profile page
          <div className="profile-page">
            <div className="profile-page-header">
              <button 
                className="back-btn" 
                onClick={() => setShowProfile(false)}
                aria-label="Back to dashboard"
              >
                ← Back
              </button>
              <h2>Profile Information</h2>
            </div>
            <div className="profile-page-content">
              <div className="profile-card-large">
                <div className="profile-header-large">
                  <div className="profile-avatar-large">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="profile-info-large">
                    <div className="profile-name-large">{user?.name}</div>
                    <div className="profile-email-large">{user?.email}</div>
                    <div className="profile-role-large">
                      <span className="role-badge-large">{user?.role}</span>
                    </div>
                  </div>
                </div>
                <div className="profile-details-large">
                  <div className="profile-section">
                    <h3>Contact Information</h3>
                    <div className="profile-detail-large">
                      <div className="detail-label">Email Address</div>
                      <div className="detail-value">{user?.email}</div>
                    </div>
                  </div>
                  <div className="profile-section">
                    <h3>Account Information</h3>
                    <div className="profile-detail-large">
                      <div className="detail-label">User Role</div>
                      <div className="detail-value">
                        <span className="role-badge" style={{ textTransform: 'uppercase' }}>{user?.role}</span>
                      </div>
                    </div>
                    <div className="profile-detail-large">
                      <div className="detail-label">Account Status</div>
                      <div className="detail-value">
                        <span className={`status-badge ${(user?.status === 'Inactive') ? 'inactive' : 'active'}`}>
                          {(user?.status === 'Inactive') ? 'Inactive' : 'Active'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : showActivity ? (
          // Activity page (mirror profile layout so content starts below white header)
          <div className="profile-page">
            <div className="profile-page-header">
              <button
                className="back-btn"
                onClick={() => setShowActivity(false)}
                aria-label="Back to dashboard"
              >
                ← Back
              </button>
              <h2>Trips History</h2>
            </div>
            <div className="profile-page-content">
              {tripHistory.length === 0 ? (
                <p className="no-trips-message">No past trips yet.</p>
              ) : (
                <div className="trips-list">
                  {tripHistory.map((t) => (
                    <div
                      key={t.id}
                      className="trip-card"
                      onClick={() => setSelectedHistoryTrip(t)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="trip-header">
                        <div className="trip-info">
                          <div className="driver-name">
                            {t.riderBoardingLocationName || t.riderBoardingLocation || t.startLocationName || t.startLocation || 'Start'} → {t.riderAlightingLocationName || t.riderAlightingLocation || t.endLocationName || t.endLocation || 'End'}
                          </div>
                          <div className="ride-code">{formatDateTime(t.startTime)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : isInitializing ? (
          <div className="status-section">
            <h2>Loading...</h2>
            <p>Checking for active trips...</p>
          </div>
        ) : currentTrip ? (
          // Current trip view
          !showTripDetails && (
            <div className="status-section">
              <h2>Current Trip</h2>
              <div className="trip-overview">
                <div className="ride-code-card">
                  <h3>Ride Code</h3>
                  <p className="ride-code-display">{currentTrip.rideCode}</p>
                  <p className="ride-code-hint">Share this code with other riders</p>
                </div>
                <div className="trip-details-card">
                  <h3>Trip Details</h3>
                  <div className="details-list">
                    <div className="detail-item">
                      <strong>Driver:</strong> <span>{currentTrip.driver.name}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Cab:</strong> <span>{currentTrip.cab.plateNumber}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Start:</strong> <span>{currentTrip.startLocationName || currentTrip.startLocation}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Started:</strong> <span>{formatDateTime(currentTrip.startTime)}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Status:</strong>{' '}
                      {userTrip ? (
                        <span className={`status-active`}>{userTrip.status}</span>
                      ) : (
                        <span className="status-active">{currentTrip.state}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {userTrip && (
                <div className="user-trip-status">
                  <h3>Your Trip Status</h3>
                  <div className="status-details">
                    <div className="status-item">
                      <strong>Status:</strong> 
                      <span className={`status-badge ${userTrip.status === 'joined' ? 'active' : 'inactive'}`}>
                        {userTrip.status === 'joined' ? 'Active' : userTrip.status}
                      </span>
                    </div>
                    <div className="status-item">
                      <strong>Boarded:</strong> <span>{formatDateTime(userTrip.boardingTime)}</span>
                    </div>
                    <div className="status-item">
                      <strong>Boarding Location:</strong> <span>{userTrip.boardingLocationName || userTrip.boardingLocation}</span>
                    </div>
                    {userTrip.alightingTime && (
                      <>
                        <div className="status-item">
                          <strong>Alighted:</strong> <span>{formatDateTime(userTrip.alightingTime)}</span>
                        </div>
                        <div className="status-item">
                          <strong>Alighting Location:</strong> <span>{userTrip.alightingLocationName || userTrip.alightingLocation}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              {userTrip && userTrip.status === "joined" && (
                <div className="alight-section">
                  <label>Alight from Trip</label>
                  {!confirmAlightOpen && (
                    <button
                      className="danger-btn"
                      onClick={() => setConfirmAlightOpen(true)}
                      disabled={isLoading}
                    >
                      {isLoading ? "Alighting..." : "Alight from Trip"}
                    </button>
                  )}
                  {confirmAlightOpen && (
                    <div style={{
                      marginTop: 10,
                      display: 'grid',
                      gap: 8,
                      background: 'var(--bg-tertiary)',
                      padding: '12px',
                      border: '1px solid var(--border-light)',
                      borderRadius: 'var(--radius-md)'
                    }}>
                      <div style={{fontSize: 14, color: 'var(--text-secondary)'}}>
                        Alight now? This will end your ride.
                      </div>
                      <div style={{display: 'flex', gap: 8}}>
                        <button
                          className="secondary-btn"
                          onClick={() => setConfirmAlightOpen(false)}
                          disabled={isLoading}
                          style={{flex:1}}
                        >
                          Cancel
                        </button>
                        <button
                          className="danger-btn"
                          onClick={() => { setConfirmAlightOpen(false); alightFromTrip(); }}
                          disabled={isLoading}
                          style={{flex:1}}
                        >
                          {isLoading ? 'Processing…' : 'Confirm Alight'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Browse ongoing trips (always visible) */}
              <div className="ongoing-trips-section" style={{ marginTop: 24 }}>
                <h3>Browse Ongoing Trips</h3>
                {ongoingTrips.length === 0 ? (
                  <p className="no-trips-message">No ongoing trips at the moment.</p>
                ) : (
                  <div className="trips-list">
                    {ongoingTrips.map((t) => {
                      const joined = typeof (t as any).joinedCount === 'number' ? (t as any).joinedCount as number : 0;
                      const capacity = (t as any)?.cab?.capacity ?? 0;
                      const seatsAvailable = Math.max(0, capacity - joined);
                      return (
                        <div key={t.id} className="trip-card">
                          <div className="trip-header">
                            <div className="trip-info">
                              <div className="driver-name">{t.driver?.name || 'Driver'}</div>
                              <div className="ride-code"><strong>Start Location:</strong> {t.startLocationName || t.startLocation || '-'}</div>
                              <div className="ride-code"><strong>Start Time:</strong> {formatDateTime(t.startTime)}</div>
                              <div className="ride-code"><strong>Cab Plate:</strong> {(t as any)?.cab?.plateNumber || '-'}</div>
                              <div className="ride-code"><strong>Seats Available:</strong> {isNaN(seatsAvailable) ? '-' : seatsAvailable} / {capacity || '-'}</div>
                            </div>
                            <button
                              className="view-map-btn"
                              onClick={() => {
                                if (selectedTripId === t.id) {
                                  setSelectedTripId(null);
                                } else {
                                  setSelectedTripId(t.id);
                                }
                              }}
                            >
                              {selectedTripId === t.id ? 'Hide Map' : 'View Route'}
                            </button>
                          </div>
                          {selectedTripId === t.id && (
                            <div className="map-container">
                              {selectedTripCoords.length >= 1 ? (
                                <TripPathMap
                                  coords={selectedTripCoords}
                                  height={280}
                                  endIconUrl={'/markers/taxi-marker.svg'}
                                  endIconSize={[36, 36]}
                                />
                              ) : (
                                <div className="no-trips-message">Loading route…</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )
        ) : (
          // Default dashboard view (no current trip)
          <div className="status-section">
            <h2>Join a Trip</h2>
            <p className="section-description">
              Enter the ride code provided by the driver to join their trip
            </p>
            <div className="join-trip-form">
              <div className="input-group">
                <label>Ride Code</label>
                <div className="input-with-button">
                  <input
                    type="text"
                    value={rideCode}
                    onChange={(e) => setRideCode(e.target.value)}
                    placeholder="Enter ride code or scan QR"
                    className="ride-code-input"
                  />
                  <button
                    type="button"
                    onClick={() => setIsScannerOpen(true)}
                    className="scan-qr-btn"
                  >
                    Scan QR
                  </button>
                </div>
                <small className="input-hint">
                  You can either scan the driver's QR or enter the ride code manually
                </small>
              </div>
              <button
                className="primary-btn"
                onClick={joinTrip}
                disabled={isLoading || !rideCode}
              >
                {isLoading ? "Joining..." : "Join Trip"}
              </button>
            </div>
            {/* Browse ongoing trips (always visible, also shown in current trip view) */}
            <div className="ongoing-trips-section">
              <h3>Browse Ongoing Trips</h3>
              {ongoingTrips.length === 0 ? (
                <p className="no-trips-message">No ongoing trips at the moment.</p>
              ) : (
                <div className="trips-list">
                  {ongoingTrips.map((t) => {
                    const joined = typeof (t as any).joinedCount === 'number' ? (t as any).joinedCount as number : 0;
                    const capacity = (t as any)?.cab?.capacity ?? 0;
                    const seatsAvailable = Math.max(0, capacity - joined);
                    return (
                      <div key={t.id} className="trip-card">
                        <div className="trip-header">
                          <div className="trip-info">
                            <div className="driver-name">{t.driver?.name || 'Driver'}</div>
                            <div className="ride-code"><strong>Start Location:</strong> {t.startLocationName || t.startLocation || '-'}</div>
                            <div className="ride-code"><strong>Start Time:</strong> {formatDateTime(t.startTime)}</div>
                            <div className="ride-code"><strong>Cab Plate:</strong> {(t as any)?.cab?.plateNumber || '-'}</div>
                            <div className="ride-code"><strong>Seats Available:</strong> {isNaN(seatsAvailable) ? '-' : seatsAvailable} / {capacity || '-'}</div>
                          </div>
                          <button
                            className="view-map-btn"
                            onClick={() => {
                              if (selectedTripId === t.id) {
                                setSelectedTripId(null);
                              } else {
                                setSelectedTripId(t.id);
                              }
                            }}
                          >
                            {selectedTripId === t.id ? 'Hide Map' : 'View Route'}
                          </button>
                        </div>
                        {selectedTripId === t.id && (
                          <div className="map-container">
                            {selectedTripCoords.length >= 1 ? (
                              <TripPathMap
                                coords={selectedTripCoords}
                                height={280}
                                endIconUrl={'/markers/taxi-marker.svg'}
                                endIconSize={[36, 36]}
                              />
                            ) : (
                              <div className="no-trips-message">Loading route…</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        

        {/* QR Scanner Modal */}
        {isScannerOpen && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setIsScannerOpen(false)}
          >
            <div
              style={{
                background: '#0b1220',
                borderRadius: '12px',
                padding: '16px',
                width: 'min(520px, 92vw)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ color: 'white', margin: 0 }}>Scan Ride QR</h3>
                <button
                  onClick={() => setIsScannerOpen(false)}
                  style={{
                    background: 'transparent',
                    border: '1px solid #334155',
                    color: '#e2e8f0',
                    padding: '6px 10px',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                >
                  ✕ Close
                </button>
              </div>
              <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1px solid #1f2937' }}>
                <div id="qr-reader" style={{ width: '100%', height: 360, background: '#111827' }} />
              </div>
              <p style={{ color: '#94a3b8', marginTop: 10, fontSize: 14 }}>
                Align the QR code within the frame to scan
              </p>
            </div>
          </div>
        )}

        
        

        {/* Trip Details Modal */}
        {showTripDetails && currentTrip && userTrip && (
          <div className="trip-details-modal">
            <div className="trip-details-modal-content">
              <div className="trip-details-modal-header">
                <h2>Trip Completed</h2>
                <button 
                  className="close-modal-btn" 
                  onClick={closeTripDetails}
                  aria-label="Close trip details"
                >
                  ✕
                </button>
              </div>
              
              <div className="trip-details-modal-body">
                <div className="trip-overview">
                  <div className="ride-code-card">
                    <h3>Ride Code</h3>
                    <p className="ride-code-display">{currentTrip.rideCode}</p>
                    <p className="ride-code-hint">Trip completed successfully</p>
                  </div>

                  <div className="trip-details-card">
                    <h3>Trip Details</h3>
                    <div className="details-list">
                      <div className="detail-item">
                        <strong>Driver:</strong> <span>{currentTrip.driver.name}</span>
                      </div>
                      <div className="detail-item">
                        <strong>Cab:</strong> <span>{currentTrip.cab.plateNumber}</span>
                      </div>
                      <div className="detail-item">
                        <strong>Start:</strong> <span>{currentTrip.startLocationName || currentTrip.startLocation}</span>
                      </div>
                      <div className="detail-item">
                        <strong>Started:</strong> <span>{formatDateTime(currentTrip.startTime)}</span>
                      </div>
                      <div className="detail-item">
                        <strong>Status:</strong>{' '}
                        {userTrip ? (
                          <span className={`status-active`}>{userTrip.status}</span>
                        ) : (
                          <span className="status-active">{currentTrip.state}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="user-trip-status">
                  <h3>Your Trip Status</h3>
                  <div className="status-details">
                    <div className="status-item">
                      <strong>Status:</strong> 
                      <span className={`status-badge ${userTrip.status === 'joined' ? 'active' : 'inactive'}`}>
                        {userTrip.status === 'joined' ? 'Active' : userTrip.status}
                      </span>
                    </div>
                    <div className="status-item">
                      <strong>Boarded:</strong> <span>{formatDateTime(userTrip.boardingTime)}</span>
                    </div>
                    <div className="status-item">
                      <strong>Boarding Location:</strong> <span>{userTrip.boardingLocationName || userTrip.boardingLocation}</span>
                    </div>
                    {userTrip.alightingTime && (
                      <>
                        <div className="status-item">
                          <strong>Alighted:</strong> <span>{formatDateTime(userTrip.alightingTime)}</span>
                        </div>
                        <div className="status-item">
                          <strong>Alighting Location:</strong> <span>{userTrip.alightingLocationName || userTrip.alightingLocation}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="trip-details-modal-footer">
                <button 
                  className="primary-btn" 
                  onClick={closeTripDetails}
                >
                  Close & Return to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Review Modal */}
        {showReviewModal && (currentTrip || pendingReviewTarget) && (
          <div
            role="dialog"
            aria-modal="true"
            className="trip-details-modal"
            style={{ zIndex: 1100 }}
          >
            <div className="trip-details-modal-content">
              <div className="trip-details-modal-header">
                <h2>Rate your trip</h2>
                <button
                  className="close-modal-btn"
                  onClick={() => {
                    setShowReviewModal(false);
                    // If this review was opened from history, don't switch to trip completion summary
                    if (!pendingReviewTarget) {
                      setShowTripDetails(true);
                    }
                    setPendingReviewTarget(null);
                  }}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <div className="trip-details-modal-body">
                <div style={{ display: 'grid', gap: 12 }}>
                  <div style={{ fontSize: 14, color: '#94a3b8' }}>
                    How was your ride with <strong style={{ color: 'var(--primary-teal)' }}>{(currentTrip as any)?.driver?.name || pendingReviewTarget?.driverName || 'the driver'}</strong>?
                  </div>
                  {/* Stars */}
                  <div style={{ display: 'flex', gap: 8, fontSize: 28 }}>
                    {[1,2,3,4,5].map((s) => (
                      <button
                        key={s}
                        onClick={() => setReviewRating(s)}
                        aria-label={`${s} star${s>1?'s':''}`}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 28,
                          padding: 6,
                          color: s <= reviewRating ? '#fbbf24' : '#475569',
                        }}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                  {/* Comment */}
                  <div style={{ display: 'grid', gap: 6 }}>
                    <label style={{ color: '#cbd5e1', fontSize: 14 }}>Comments (optional)</label>
                    <textarea
                      value={reviewComment}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.length <= 500) setReviewComment(val);
                      }}
                      placeholder="Share feedback to help improve rides (max 500 chars)"
                      style={{
                        background: '#0b1220',
                        color: '#e2e8f0',
                        border: '1px solid #1f2937',
                        borderRadius: 8,
                        padding: 10,
                        minHeight: 90,
                        resize: 'vertical',
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94a3b8' }}>
                      <span>{reviewComment.length}/500</span>
                      <span>Rating required</span>
                    </div>
                  </div>
                  {reviewError && (
                    <div style={{ color: '#fca5a5', fontSize: 13 }}>{reviewError}</div>
                  )}
                </div>
              </div>
              <div className="trip-details-modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  className="secondary-btn"
                  onClick={() => {
                    setShowReviewModal(false);
                    if (!pendingReviewTarget) {
                      setShowTripDetails(true);
                    }
                    setPendingReviewTarget(null);
                  }}
                  style={{ borderRadius: 'var(--radius-md)' }}
                >
                  Skip
                </button>
                <button
                  className="primary-btn"
                  onClick={submitReview}
                  disabled={isSubmittingReview || reviewRating < 1}
                >
                  {isSubmittingReview ? 'Submitting…' : 'Submit Review'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* History Trip Details Modal */}
        {selectedHistoryTrip && (
          <div className="trip-details-modal">
            <div className="trip-details-modal-content">
              <div className="trip-details-modal-header">
                <h2>Trip Details</h2>
                <button 
                  className="close-modal-btn" 
                  onClick={() => setSelectedHistoryTrip(null)}
                  aria-label="Close trip details"
                >
                  ✕
                </button>
              </div>
              <div className="trip-details-modal-body">
                <div className="trip-overview">
                  <div className="trip-details-card" style={{ width: '100%', gridColumn: '1 / -1' }}>
                    <h3>Route</h3>
                    <div className="live-map-container">
                      <TripPathMap
                        coords={historyTripLogs
                          .map(l => parseLocation(l.location) || null)
                          .filter((c): c is { lat: number; lng: number } => !!c)
                          .map(c => [c.lat, c.lng])}
                        height={280}
                      />
                    </div>
                  </div>
                </div>
                <div className="user-trip-status" style={{ marginTop: 12 }}>
                  <h3>Summary</h3>
                  <div className="status-details">
                    <div className="status-item"><strong>Date:</strong> <span>{formatDate(selectedHistoryTrip.serviceDate || selectedHistoryTrip.startTime)}</span></div>
                    <div className="status-item"><strong>Start:</strong> <span>{historyUserTrip?.boardingLocationName || selectedHistoryTrip.riderBoardingLocationName || selectedHistoryTrip.startLocationName || historyUserTrip?.boardingLocation || selectedHistoryTrip.riderBoardingLocation || selectedHistoryTrip.startLocation || '-'}</span></div>
                    <div className="status-item"><strong>End:</strong> <span>{historyUserTrip?.alightingLocationName || selectedHistoryTrip.riderAlightingLocationName || selectedHistoryTrip.endLocationName || historyUserTrip?.alightingLocation || selectedHistoryTrip.riderAlightingLocation || selectedHistoryTrip.endLocation || '-'}</span></div>
                    {/* If rider-specific end location is missing but logs exist, fallback to last logged location */}
                    {!historyUserTrip?.alightingLocation && !selectedHistoryTrip.riderAlightingLocation && !selectedHistoryTrip.endLocation && (
                      <div className="status-item"><strong>End (derived):</strong> <span>{historyTripLogs.length ? (historyTripLogs[historyTripLogs.length - 1] as any)?.locationName || historyTripLogs[historyTripLogs.length - 1]?.location : '-'}</span></div>
                    )}
                    {historyMetrics && (
                      <>
                        <div className="status-item"><strong>Distance:</strong> <span>{historyMetrics.distanceKm} km</span></div>
                      </>
                    )}
                    <div className="status-item"><strong>Pickup Time:</strong> <span>{formatDateTime(historyUserTrip?.boardingTime || selectedHistoryTrip.riderBoardingTime || selectedHistoryTrip.startTime)}</span></div>
                    {(historyUserTrip?.alightingTime || selectedHistoryTrip.riderAlightingTime || selectedHistoryTrip.endTime) && (
                      <div className="status-item"><strong>Drop Time:</strong> <span>{formatDateTime(historyUserTrip?.alightingTime || (selectedHistoryTrip.riderAlightingTime as string) || (selectedHistoryTrip.endTime as string))}</span></div>
                    )}
                    {historyMetrics && (
                      <>
                        <div className="status-item"><strong>Duration:</strong> <span>{historyMetrics.durationMin} min</span></div>
                      </>
                    )}
                  </div>
                </div>

                {/* Review section: show existing review or allow adding one */}
                <div className="user-trip-status" style={{ marginTop: 12 }}>
                  <h3>Your Review</h3>
                  {historyTripDetails ? (
                    historyReview ? (
                      <div className="status-details">
                        <div className="status-item">
                          <strong>Rating:</strong>{' '}
                          <span>
                            {Array.from({ length: 5 }).map((_, i) => (
                              <span key={i} style={{ color: i < historyReview.rating ? '#fbbf24' : '#475569', marginRight: 2 }}>★</span>
                            ))}
                          </span>
                        </div>
                        {historyReview.comment && (
                          <div className="status-item">
                            <strong>Comment:</strong> <span>{historyReview.comment}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        className="primary-btn"
                        onClick={() => {
                          const dId = ((historyTripDetails as any)?.driver?.id) || ((historyTripDetails as any)?.driverId);
                          const dName = (historyTripDetails as any)?.driver?.name;
                          if (dId) {
                            setPendingReviewTarget({ driverId: dId, driverName: dName, tripId: selectedHistoryTrip.id });
                            setShowReviewModal(true);
                          } else {
                            showNotice('Unable to identify driver for this trip.', 'error');
                          }
                        }}
                      >
                        Add Review
                      </button>
                    )
                  ) : (
                    <div className="status-details"><div className="status-item">Loading trip details…</div></div>
                  )}
                </div>
              </div>
              <div className="trip-details-modal-footer">
                <button 
                  className="primary-btn" 
                  onClick={() => setSelectedHistoryTrip(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiderDashboard;
